import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { api } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Truck, ArrowLeft, CreditCard, Shield, Check, Star, Zap, Crown, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const PLANS = [
  { key: 'basic', name: 'Basic Franchise', setup: 100, monthly: 30, icon: Star, color: 'slate', features: ['Up to 2 Agents', 'Load Management', 'Carrier Database', 'Basic Reporting'] },
  { key: 'pro', name: 'Pro Franchise', setup: 150, monthly: 45, icon: Zap, color: 'blue', badge: 'MOST POPULAR', features: ['Up to 5 Agents', 'Everything in Basic', 'AI Document Scanner', 'In-App Messaging', 'Driver Portal'] },
  { key: 'extreme', name: 'Extreme Franchise', setup: 250, monthly: 75, icon: Crown, color: 'purple', features: ['Unlimited Agents', 'Everything in Pro', 'Credit Checks', 'Priority Support', 'Settlements & Compliance'] },
];

const COLOR_MAP = {
  slate:  { ring: 'ring-slate-400',  bg: 'bg-slate-50',  text: 'text-slate-700',  btn: 'bg-slate-700 hover:bg-slate-800',  icon: 'text-slate-600',  badge: '' },
  blue:   { ring: 'ring-blue-500',   bg: 'bg-blue-50',   text: 'text-blue-600',   btn: 'bg-blue-600 hover:bg-blue-700',    icon: 'text-blue-600',   badge: 'bg-blue-600' },
  purple: { ring: 'ring-purple-500', bg: 'bg-purple-50', text: 'text-purple-600', btn: 'bg-purple-600 hover:bg-purple-700', icon: 'text-purple-600', badge: 'bg-purple-600' },
};

let stripePromise = null;
function getStripe(publishableKey) {
  if (!stripePromise && publishableKey) {
    stripePromise = loadStripe(publishableKey);
  }
  return stripePromise;
}

export default function SignUp() {
  const [params] = useSearchParams();
  const preselected = params.get('plan');
  const preselectedAddon = params.get('addon') === 'phase3';

  const [step, setStep] = useState(preselected ? 2 : 1);
  const [selectedPlan, setSelectedPlan] = useState(preselected || '');
  const [addonPhase3, setAddonPhase3] = useState(preselectedAddon && preselected === 'extreme');
  const [form, setForm] = useState({ company_name: '', admin_name: '', city: '', state: '', zip_code: '', email: '', phone: '' });
  const [stripeKey, setStripeKey] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [checkoutError, setCheckoutError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/payments/config')
      .then(res => setStripeKey(res.data.publishable_key))
      .catch(() => {});
  }, []);

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const selectPlan = (key) => {
    setSelectedPlan(key);
    if (key !== 'extreme') setAddonPhase3(false);
    setStep(2);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    for (const [key, val] of Object.entries(form)) {
      if (!val.trim()) {
        toast.error(`Please fill in ${key.replace(/_/g, ' ')}`);
        return;
      }
    }
    if (!stripeKey) {
      toast.error('Payment system is not configured. Please contact support.');
      return;
    }
    setLoading(true);
    setCheckoutError('');
    try {
      const res = await api.post('/payments/checkout', {
        ...form,
        plan: selectedPlan,
        addon_phase3: addonPhase3,
        origin_url: window.location.origin,
      });
      setClientSecret(res.data.client_secret);
      setStep(3);
    } catch (err) {
      setCheckoutError(err.response?.data?.detail || 'Failed to initialize payment. Please try again.');
      toast.error(err.response?.data?.detail || 'Payment initialization failed.');
    } finally {
      setLoading(false);
    }
  };

  const planInfo = PLANS.find(p => p.key === selectedPlan);
  const stepLabels = ['Select Plan', 'Company Info', 'Payment'];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800" data-testid="signup-page">
      {/* Nav */}
      <nav className="border-b border-white/10 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="bg-blue-600 p-1.5 rounded-lg"><Truck className="w-5 h-5 text-white" /></div>
            <span className="font-bold text-lg text-white tracking-tight">Urbanvision Logistics</span>
          </div>
          <a href="/pricing" className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-1" data-testid="back-link">
            <ArrowLeft className="w-4 h-4" /> Back to Pricing
          </a>
        </div>
      </nav>

      {/* Step indicator */}
      <div className="max-w-5xl mx-auto px-4 pt-8 pb-4">
        <div className="flex items-center justify-center gap-2" data-testid="step-indicator">
          {stepLabels.map((label, i) => {
            const stepNum = i + 1;
            const active = step === stepNum;
            const done = step > stepNum;
            return (
              <div key={label} className="flex items-center gap-2">
                {i > 0 && <div className={`w-8 h-px ${done || active ? 'bg-blue-500' : 'bg-slate-600'}`} />}
                <div className="flex items-center gap-1.5">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                    done ? 'bg-blue-600 text-white' : active ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400'
                  }`}>{done ? <Check className="w-3.5 h-3.5" /> : stepNum}</div>
                  <span className={`text-xs font-medium hidden sm:inline ${active || done ? 'text-white' : 'text-slate-500'}`}>{label}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="px-4 pb-16">
        {/* ---- STEP 1: Plan Selection ---- */}
        {step === 1 && (
          <div className="max-w-4xl mx-auto" data-testid="step-plan-selection">
            <div className="text-center mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">Choose Your Plan</h1>
              <p className="text-slate-400 text-sm">Select a franchise tier to get started.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {PLANS.map(plan => {
                const c = COLOR_MAP[plan.color];
                const Icon = plan.icon;
                return (
                  <Card
                    key={plan.key}
                    data-testid={`plan-card-${plan.key}`}
                    className={`cursor-pointer transition-all hover:scale-[1.02] relative border-2 ${
                      selectedPlan === plan.key ? `${c.ring} ring-2` : 'border-slate-200'
                    }`}
                    onClick={() => selectPlan(plan.key)}
                  >
                    {plan.badge && (
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
                        <span className={`${c.badge} text-white px-3 py-0.5 rounded-full text-[10px] font-bold`}>{plan.badge}</span>
                      </div>
                    )}
                    <CardContent className="pt-6 pb-5">
                      <div className="flex items-center gap-2 mb-3">
                        <Icon className={`w-5 h-5 ${c.icon}`} />
                        <span className="font-semibold text-slate-900">{plan.name}</span>
                      </div>
                      <p className="text-xs text-slate-500 mb-1">${plan.setup} one-time setup</p>
                      <div className="mb-4">
                        <span className={`text-3xl font-bold ${c.text}`}>${plan.monthly}</span>
                        <span className="text-slate-500 text-sm">/mo</span>
                      </div>
                      <ul className="space-y-2 mb-4">
                        {plan.features.map(f => (
                          <li key={f} className="flex items-start gap-1.5 text-sm text-slate-600">
                            <Check className="w-3.5 h-3.5 text-green-600 mt-0.5 flex-shrink-0" />{f}
                          </li>
                        ))}
                      </ul>
                      <Button className={`w-full ${c.btn} text-white font-semibold`} data-testid={`select-plan-${plan.key}-btn`}>
                        Select {plan.name.split(' ')[0]}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* ---- STEP 2: Company Info ---- */}
        {step === 2 && planInfo && (
          <div className="max-w-lg mx-auto" data-testid="step-company-info">
            <div className="text-center mb-6">
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${COLOR_MAP[planInfo.color].bg} ${COLOR_MAP[planInfo.color].text}`}>
                {planInfo.name}
              </span>
              <h1 className="text-3xl font-bold text-white mt-3 mb-1">Company Details</h1>
              <p className="text-slate-400 text-sm">Fill in your information, then proceed to payment.</p>
            </div>

            <Card className="shadow-xl">
              <CardContent className="pt-6">
                <form onSubmit={handleFormSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="company_name">Company Name</Label>
                    <Input id="company_name" data-testid="input-company-name" placeholder="Your company name" value={form.company_name} onChange={set('company_name')} />
                  </div>
                  <div>
                    <Label htmlFor="admin_name">Master Admin Name</Label>
                    <Input id="admin_name" data-testid="input-admin-name" placeholder="Full name" value={form.admin_name} onChange={set('admin_name')} />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label htmlFor="city">City</Label><Input id="city" data-testid="input-city" placeholder="City" value={form.city} onChange={set('city')} /></div>
                    <div><Label htmlFor="state">State</Label><Input id="state" data-testid="input-state" placeholder="State" value={form.state} onChange={set('state')} /></div>
                    <div><Label htmlFor="zip_code">Zip</Label><Input id="zip_code" data-testid="input-zip" placeholder="Zip" value={form.zip_code} onChange={set('zip_code')} /></div>
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" data-testid="input-email" type="email" placeholder="your@email.com" value={form.email} onChange={set('email')} />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input id="phone" data-testid="input-phone" type="tel" placeholder="(555) 123-4567" value={form.phone} onChange={set('phone')} />
                  </div>

                  {/* Phase 3 Add-On (Extreme only) */}
                  {selectedPlan === 'extreme' && (
                    <div
                      className={`rounded-lg p-3 border-2 transition-all ${
                        addonPhase3
                          ? 'bg-amber-50 border-amber-400'
                          : 'bg-slate-50 border-slate-200 hover:border-amber-300'
                      }`}
                      data-testid="phase3-addon-toggle"
                    >
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 accent-amber-500"
                          checked={addonPhase3}
                          onChange={(e) => setAddonPhase3(e.target.checked)}
                          data-testid="phase3-addon-checkbox"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-slate-900">Phase 3 AI — Autonomous Dispatch</span>
                            <span className="bg-amber-500 text-slate-900 text-[10px] font-bold px-1.5 py-0.5 rounded">
                              ADD-ON
                            </span>
                          </div>
                          <p className="text-xs text-slate-600 mt-0.5">
                            Auto-books loads, matches backhauls, enforces $/mi rules.
                          </p>
                          <p className="text-xs font-medium text-amber-700 mt-1">
                            +$150 setup · +$75/mo
                          </p>
                        </div>
                      </label>
                    </div>
                  )}

                  {/* Payment summary */}
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200" data-testid="payment-summary">
                    <p className="text-sm font-medium text-slate-700 mb-2">Payment Summary</p>
                    <div className="flex justify-between text-sm text-slate-600">
                      <span>Setup Fee — {planInfo.name}</span>
                      <span>${planInfo.setup}.00</span>
                    </div>
                    {addonPhase3 && selectedPlan === 'extreme' && (
                      <div className="flex justify-between text-sm text-amber-700" data-testid="summary-addon-setup">
                        <span>Setup Fee — Phase 3 AI Add-On</span>
                        <span>+$150.00</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm text-slate-500 mt-1">
                      <span>Monthly Subscription (starts in 30 days)</span>
                      <span>${planInfo.monthly}.00/mo</span>
                    </div>
                    {addonPhase3 && selectedPlan === 'extreme' && (
                      <div className="flex justify-between text-sm text-amber-700" data-testid="summary-addon-monthly">
                        <span>Monthly — Phase 3 AI Add-On</span>
                        <span>+$75.00/mo</span>
                      </div>
                    )}
                    <div className="border-t border-slate-200 mt-2 pt-2 flex justify-between font-bold text-slate-900">
                      <span>Due Today</span>
                      <span>
                        ${(planInfo.setup + (addonPhase3 && selectedPlan === 'extreme' ? 150 : 0)).toFixed(2)}
                      </span>
                    </div>
                    {addonPhase3 && selectedPlan === 'extreme' && (
                      <div className="border-t border-dashed border-slate-200 mt-2 pt-2 flex justify-between text-xs text-slate-500">
                        <span>Total monthly starting day 30</span>
                        <span>${(planInfo.monthly + 75).toFixed(2)}/mo</span>
                      </div>
                    )}
                  </div>

                  {checkoutError && (
                    <p className="text-red-500 text-sm" data-testid="checkout-error">{checkoutError}</p>
                  )}

                  <div className="flex gap-3 pt-2">
                    <Button type="button" variant="outline" className="flex-1" onClick={() => { setStep(1); setSelectedPlan(''); }} data-testid="back-to-plans-btn">
                      <ArrowLeft className="w-4 h-4 mr-1" /> Change Plan
                    </Button>
                    <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold" disabled={loading} data-testid="continue-to-payment-btn">
                      {loading ? (
                        <span className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Loading...</span>
                      ) : (
                        <span className="flex items-center gap-2">Continue to Payment <ArrowRight className="w-4 h-4" /></span>
                      )}
                    </Button>
                  </div>

                  <div className="flex items-center justify-center gap-1.5 text-xs text-slate-400">
                    <Shield className="w-3 h-3" />
                    <span>Secured by Stripe. Card details never touch our servers.</span>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ---- STEP 3: Embedded Stripe Checkout ---- */}
        {step === 3 && clientSecret && stripeKey && (
          <div className="max-w-lg mx-auto" data-testid="step-payment">
            <div className="text-center mb-6">
              <h1 className="text-3xl font-bold text-white mb-1">Complete Payment</h1>
              <p className="text-slate-400 text-sm">Enter your card details below to finalize your {planInfo?.name || 'franchise'}.</p>
            </div>
            <Card className="shadow-xl overflow-hidden">
              <CardContent className="p-0">
                <EmbeddedCheckoutProvider stripe={getStripe(stripeKey)} options={{ clientSecret }}>
                  <EmbeddedCheckout data-testid="stripe-embedded-checkout" />
                </EmbeddedCheckoutProvider>
              </CardContent>
            </Card>
            <div className="text-center mt-4">
              <button
                onClick={() => { setStep(2); setClientSecret(null); }}
                className="text-sm text-slate-400 hover:text-white transition-colors"
                data-testid="back-to-form-btn"
              >
                <ArrowLeft className="w-3 h-3 inline mr-1" />Back to company details
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
